import { useQuery } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Loader2, GitBranch, ExternalLink } from "lucide-react";
import { getCoilTree, singleLabelPdfUrl } from "@/lib/api";
import { fmtKg } from "@/lib/utils";

const StatusBadge = ({ status }) => {
  const map = {
    pending: "bg-amber-50 text-amber-800 border-amber-200",
    received: "bg-green-50 text-green-800 border-green-200",
    consumed: "bg-slate-100 text-slate-600 border-slate-200",
    dispatched: "bg-blue-50 text-blue-800 border-blue-200",
  };
  return (
    <span className={`inline-flex items-center text-[10px] tracking-wider uppercase font-semibold px-2 py-0.5 rounded-sm border ${map[status] || map.received}`}>
      {status}
    </span>
  );
};

const CoilTreeDialog = ({ open, onOpenChange, coilId }) => {
  const { data, isLoading } = useQuery({
    queryKey: ["coil-tree", coilId],
    queryFn: () => getCoilTree(coilId),
    enabled: !!coilId && open,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="coil-tree-dialog" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <GitBranch className="h-5 w-5" /> Slit Tree
          </DialogTitle>
        </DialogHeader>

        {isLoading || !data ? (
          <div className="py-12 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-5 py-2">
            {/* Mother */}
            <div className="bg-slate-50 border border-slate-200 rounded-sm p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">RM</p>
                  <h4 className="font-mono-data text-lg font-bold text-slate-900">{data.mother?.coil_id}</h4>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={data.mother?.status} />
                  {data.mother?.id && (
                    <a href={singleLabelPdfUrl(data.mother.id)} target="_blank" rel="noreferrer" className="text-xs text-blue-700 hover:underline inline-flex items-center gap-1">
                      Label <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-xs">
                {[
                  ["Supplier", data.mother?.supplier || "—"],
                  ["Grade", data.mother?.grade || "—"],
                  ["Thickness", `${data.mother?.thickness_mm || "—"} mm`],
                  ["Width", `${data.mother?.width_mm || "—"} mm`],
                  ["Net Wt", `${fmtKg(data.mother?.weight_kg || 0)} kg`],
                  ["Pack List", data.mother?.packaging_list_number || "—"],
                  ["Location", data.mother?.location || "—"],
                  ["Consumed", (data.mother?.consumed_at || "").slice(0, 10) || "—"],
                ].map(([k, v]) => (
                  <div key={k}>
                    <p className="text-[9px] tracking-wider uppercase text-slate-500">{k}</p>
                    <p className="font-mono-data text-slate-900">{v}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Batch */}
            {data.batch && (
              <div data-testid="tree-batch-stats" className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[
                  ["SFGs produced", data.batch.sfg_count],
                  ["Total SFG Wt", `${fmtKg(data.batch.total_slit_weight_kg || 0)} kg`],
                  ["Theoretical Yield", `${data.batch.theoretical_yield_pct ?? "—"}%`],
                  ["Actual Yield", data.batch.actual_yield_pct != null ? `${data.batch.actual_yield_pct}%` : "Not reconciled"],
                ].map(([k, v]) => (
                  <div key={k} className="border border-slate-100 bg-white px-3 py-2 rounded-sm">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">{k}</p>
                    <p className="font-mono-data text-sm font-semibold text-slate-900 mt-0.5">{v}</p>
                  </div>
                ))}
                {data.batch.scrap_kg != null && (
                  <div className="border border-amber-100 bg-amber-50 px-3 py-2 rounded-sm sm:col-span-2">
                    <p className="text-[10px] tracking-[0.2em] uppercase text-amber-700 font-semibold">Scrap recorded</p>
                    <p className="font-mono-data text-sm font-semibold text-amber-900 mt-0.5">
                      {Number(data.batch.scrap_kg).toLocaleString()} kg · reconciled by {data.batch.reconciled_by || "—"}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* SFG children */}
            <div>
              <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold mb-2">
                SFG Children ({data.sfgs?.length || 0})
              </p>
              {(data.sfgs || []).length === 0 ? (
                <p className="text-sm text-slate-500 py-3 text-center bg-slate-50 rounded-sm">No SFGs produced from this RM.</p>
              ) : (
                <div className="border border-slate-200 rounded-sm overflow-hidden max-h-72 overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2 text-[10px] tracking-wider uppercase text-slate-500 font-semibold">SR SFG ID</th>
                        <th className="text-left px-3 py-2 text-[10px] tracking-wider uppercase text-slate-500 font-semibold">Slit (mm)</th>
                        <th className="text-right px-3 py-2 text-[10px] tracking-wider uppercase text-slate-500 font-semibold">Net Wt</th>
                        <th className="text-left px-3 py-2 text-[10px] tracking-wider uppercase text-slate-500 font-semibold">Status</th>
                        <th className="text-left px-3 py-2 text-[10px] tracking-wider uppercase text-slate-500 font-semibold">Customer / Press</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.sfgs.map((s) => (
                        <tr key={s.id} className="border-t border-slate-100" data-testid={`tree-sfg-${s.coil_id}`}>
                          <td className="px-3 py-1.5 font-mono-data font-semibold">{s.coil_id}</td>
                          <td className="px-3 py-1.5 font-mono-data">{s.slit_width_mm}</td>
                          <td className="px-3 py-1.5 text-right font-mono-data">{fmtKg(s.weight_kg)} kg</td>
                          <td className="px-3 py-1.5"><StatusBadge status={s.status} /></td>
                          <td className="px-3 py-1.5 text-xs text-slate-700" data-testid={`tree-where-${s.coil_id}`}>
                            {s.status === "dispatched" && (
                              <span>To <b>{s.dispatched_customer || "—"}</b>{s.dispatch_order_number && <span className="text-slate-400 font-mono-data"> · {s.dispatch_order_number}</span>}</span>
                            )}
                            {s.consumed_presses?.length > 0 && (
                              <span className={s.status === "dispatched" ? "block" : ""}>
                                {s.status === "consumed" ? "Consumed on " : "Partly used on "}<b>{s.consumed_presses.join(", ")}</b>
                              </span>
                            )}
                            {s.status !== "dispatched" && !s.consumed_presses?.length && "—"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CoilTreeDialog;
